/* =========================================================================
   RBAC permission registry — the single source of truth for the permission
   architecture. Adding a new module or action here automatically flows through
   the entire UI (permission matrix, role editor, gating, summaries). This is
   what makes the system "scalable without changing the core architecture".

   When the backend is ready, the SHAPE below is what the API should mirror:
     permissions = { [moduleKey]: { [actionKey]: boolean } }
   ========================================================================= */

// Every granular action the system understands. Order here is the display
// order in the permission matrix columns.
export const ACTIONS = [
  { key: "view", label: "View", description: "See the module and its items" },
  { key: "create", label: "Create", description: "Add new items" },
  { key: "edit", label: "Edit", description: "Modify existing items" },
  { key: "delete", label: "Delete", description: "Remove items" },
  { key: "publish", label: "Publish", description: "Make items live" },
  { key: "approve", label: "Approve", description: "Review and approve submissions" },
  { key: "export", label: "Export", description: "Download / export data" },
  { key: "impersonate", label: "Impersonate", description: "Open an approved creator's portal and act as them" },
  { key: "email", label: "Email", description: "Send announcement emails to subscribers" },
];

export const ACTION_KEYS = ACTIONS.map((a) => a.key);

// Every CMS module that can be permission-controlled. `actions` declares which
// of the global ACTIONS are meaningful for that module (e.g. Analytics has no
// "publish"). To add a future module, append one entry — nothing else changes.
// Each module lists ONLY the actions the product actually has AND enforces on
// the backend. No dummy toggles — every checkbox here maps to a real, gated
// capability. (Removed earlier: a generic publish/approve/export on every module
// and a Playlists module, none of which exist in this product.)
//   - "approve" lives on Content only = moderating creator submissions
//     (approve/reject in the Submissions screen). Admin content is auto-approved.
//   - "impersonate" lives on Users only = "Open creator portal" on an approved
//     creator's detail page (act as them; enforced server-side).
//   - "email" lives on Users only = "Send email" on the Users list (announcement
//     to selected/filtered subscribers; POST /subscribers/announcement).
export const MODULES = [
  {
    key: "content",
    label: "Content",
    description: "Movies, podcasts, short clips, live events",
    actions: ["view", "create", "edit", "delete", "approve"],
  },
  {
    key: "series",
    label: "Series",
    description: "Series, seasons and episodes",
    actions: ["view", "create", "edit", "delete"],
  },
  {
    key: "media",
    label: "Media Library",
    description: "Uploaded videos and images",
    actions: ["view", "create", "delete"],
  },
  {
    key: "livetv",
    label: "Live TV",
    description: "Live channels and streams",
    actions: ["view", "create", "edit", "delete"],
  },
  {
    key: "categories",
    label: "Categories",
    description: "Genres, taxonomies and pill categories",
    actions: ["view", "create", "edit", "delete"],
  },
  {
    key: "users",
    label: "Users",
    description: "Staff accounts and roles",
    actions: ["view", "create", "edit", "delete", "impersonate", "email"],
  },
  {
    key: "settings",
    label: "Settings",
    description: "App configuration and integrations",
    actions: ["view", "edit"],
  },
  {
    key: "analytics",
    label: "Analytics",
    description: "Dashboards and reports",
    actions: ["view"],
  },
  {
    key: "monetization",
    label: "Monetization",
    description: "Ad providers and subscription plans",
    actions: ["view", "edit"],
  },
  {
    key: "support",
    label: "Support Tickets",
    description: "Creator Help & Support requests",
    actions: ["view", "edit"],
  },
  {
    key: "audit_logs",
    label: "Audit Logs",
    description: "Read-only history of admin and creator changes",
    actions: ["view"],
  },
];

export const MODULE_KEYS = MODULES.map((m) => m.key);

const MODULE_BY_KEY = MODULES.reduce((acc, m) => {
  acc[m.key] = m;
  return acc;
}, {});

export const getModule = (key) => MODULE_BY_KEY[key];

/** Total number of grantable permission cells across all modules. */
export const TOTAL_PERMISSION_COUNT = MODULES.reduce(
  (sum, m) => sum + m.actions.length,
  0
);

/** An empty permission map: every module present, every action false. */
export const emptyPermissions = () =>
  MODULES.reduce((acc, m) => {
    acc[m.key] = m.actions.reduce((a, action) => {
      a[action] = false;
      return a;
    }, {});
    return acc;
  }, {});

/** A full-access permission map (every applicable action true). */
export const fullPermissions = () =>
  MODULES.reduce((acc, m) => {
    acc[m.key] = m.actions.reduce((a, action) => {
      a[action] = true;
      return a;
    }, {});
    return acc;
  }, {});

/**
 * Normalize an arbitrary (possibly partial / legacy) permission object into the
 * canonical shape — guarantees every current module/action key exists. This is
 * the seam that keeps old saved roles working as the registry grows.
 */
export const normalizePermissions = (perms = {}) => {
  const base = emptyPermissions();
  MODULES.forEach((m) => {
    const incoming = perms[m.key] || {};
    m.actions.forEach((action) => {
      base[m.key][action] = Boolean(incoming[action]);
    });
  });
  return base;
};

/** Count granted cells in a permission map. */
export const countGranted = (perms = {}) =>
  MODULES.reduce(
    (sum, m) =>
      sum + m.actions.filter((action) => perms?.[m.key]?.[action]).length,
    0
  );

/** Merge several permission maps with OR (used to compute effective perms). */
export const mergePermissions = (...maps) => {
  const result = emptyPermissions();
  MODULES.forEach((m) => {
    m.actions.forEach((action) => {
      result[m.key][action] = maps.some((map) => map?.[m.key]?.[action]);
    });
  });
  return result;
};

/** Human summary like "Content, Series +3" for a permission map. */
export const summarizeModules = (perms = {}, max = 3) => {
  const active = MODULES.filter((m) =>
    m.actions.some((action) => perms?.[m.key]?.[action])
  ).map((m) => m.label);
  if (active.length === 0) return "No access";
  if (active.length <= max) return active.join(", ");
  return `${active.slice(0, max).join(", ")} +${active.length - max}`;
};
